/**
 * Lo que el Inicio y su precarga tienen que compartir SÍ o SÍ: la clave de caché y el mapeo del RPC.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 * La precarga (`prefetchDashboardStats`, disparada desde el Sidebar) y la pantalla calculaban su
 * rango de fechas por separado: la precarga usaba el día terminando a las 23:59:59.999 y la pantalla
 * el día normalizado a medianoche. Con la caché a mano eso ya fallaba en silencio; con TanStack Query
 * es peor, porque **la clave ES la identidad**: dos rangos distintos son dos entradas distintas y la
 * precarga calentaría una que la pantalla nunca lee. El usuario paga el RPC dos veces y no gana nada.
 *
 * 🪤 Un helper compartido no es ceremonia: es la única forma de que la clave no pueda divergir. Si
 * mañana alguien cambia el rango por defecto, lo cambia en un lado y los dos siguen coincidiendo.
 */

import { startOfDay, subDays } from 'date-fns';

export interface EstadisticasPanel {
  salesInPeriod: number;
  previousPeriodSales: number;
  ordersInPeriod: number;
  pendingOrders: number;
  totalProducts: number;
  activeProducts: number;
  totalCustomers: number;
  newCustomersInPeriod: number;
}

export interface SerieSparkline {
  sales: number[];
  orders: number[];
  pending: number[];
  products: number[];
  customers: number[];
  new_customers: number[];
}

/**
 * El rango que muestra el Inicio al abrirse: últimos 30 días.
 *
 * 🪤 Normalizado a medianoche, no a `new Date()` pelado. Sin normalizar, la clave cambiaría por
 * milisegundos en cada montaje y la caché NUNCA acertaría — una conversión que parece hecha y no
 * sirve para nada.
 */
export function rangoPorDefectoDashboard(): { start: Date; end: Date } {
  const hoy = startOfDay(new Date());
  return { start: subDays(hoy, 29), end: hoy };
}

/** La identidad de la consulta del Inicio. La usan la pantalla y la precarga; no puede haber dos. */
export function claveDashboard(
  tenantId: string | undefined,
  start: Date | undefined,
  end: Date | undefined,
  canal: string,
): readonly unknown[] {
  return ['dashboard', tenantId, start?.toISOString(), end?.toISOString(), canal];
}

/**
 * Traduce la respuesta del RPC a lo que la pantalla muestra. **Función pura**: no escribe estado ni
 * caché. Antes vivía dentro del componente y hacía las tres cosas a la vez, duplicando el mapeo —una
 * copia para la pantalla y otra para el caché a mano—, que es como se desincronizan dos vistas del
 * mismo dato.
 */
export function mapearRpcDashboard(
  rpcData: Record<string, unknown>,
): { stats: EstadisticasPanel; sparkline: SerieSparkline | null } {
  const r = rpcData as {
    sales_in_period?: number;
    sales_previous_period?: number;
    orders_in_period?: number;
    pending_orders?: number;
    total_products?: number;
    active_products?: number;
    total_customers?: number;
    new_customers_in_period?: number;
    sparkline?: {
      sales?: number[]; orders?: number[]; pending?: number[];
      products?: number[]; customers?: number[]; new_customers?: number[];
    };
  };
  const sp = r.sparkline;
  const serie = (v: number[] | undefined) => (Array.isArray(v) ? v.map(Number) : [0]);
  return {
    stats: {
      salesInPeriod: Number(r.sales_in_period ?? 0),
      previousPeriodSales: Number(r.sales_previous_period ?? 0),
      ordersInPeriod: Number(r.orders_in_period ?? 0),
      pendingOrders: Number(r.pending_orders ?? 0),
      totalProducts: Number(r.total_products ?? 0),
      activeProducts: Number(r.active_products ?? 0),
      totalCustomers: Number(r.total_customers ?? 0),
      newCustomersInPeriod: Number(r.new_customers_in_period ?? 0),
    },
    sparkline: sp
      ? {
          sales: serie(sp.sales), orders: serie(sp.orders), pending: serie(sp.pending),
          products: serie(sp.products), customers: serie(sp.customers), new_customers: serie(sp.new_customers),
        }
      : null,
  };
}
